import { useEffect, useRef, useState } from 'react';

// One initial attempt plus two automatic retries for the same line-item payload.
const MAX_INLINE_PAYMENT_RETRIES = 2;

const useInlinePaymentIntent = ({
  needsInlinePayment,
  onCreatePaymentIntent,
  currentUser,
  currentListing,
  currentProvider,
  updatedLineItems,
  transactionId,
}) => {
  const [paymentState, setPaymentState] = useState(null);
  const [isCreatingPI, setIsCreatingPI] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [paymentError, setPaymentError] = useState(null);
  const piRequestIdRef = useRef(0);
  const latestInlinePaymentPayloadKeyRef = useRef(null);
  const requestedInlinePaymentPayloadKeyRef = useRef(null);
  const previousInlinePaymentPayloadKeyRef = useRef(JSON.stringify(updatedLineItems));
  const inlinePaymentPayloadKey = JSON.stringify(updatedLineItems);

  useEffect(() => {
    latestInlinePaymentPayloadKeyRef.current = inlinePaymentPayloadKey;
  }, [inlinePaymentPayloadKey]);

  useEffect(() => {
    // A booking-change edit creates a new payload, so give it a fresh retry budget.
    if (previousInlinePaymentPayloadKeyRef.current !== inlinePaymentPayloadKey) {
      previousInlinePaymentPayloadKeyRef.current = inlinePaymentPayloadKey;
      requestedInlinePaymentPayloadKeyRef.current = null;
      setPaymentState(null);
      setPaymentError(null);
      setRetryCount(0);
      setIsCreatingPI(false);
    }
  }, [inlinePaymentPayloadKey]);

  useEffect(() => {
    if (!needsInlinePayment) {
      requestedInlinePaymentPayloadKeyRef.current = null;
      setPaymentState(null);
      setPaymentError(null);
      setRetryCount(0);
      return;
    }

    if (
      !onCreatePaymentIntent ||
      isCreatingPI ||
      paymentState?.clientSecret ||
      paymentError ||
      retryCount > MAX_INLINE_PAYMENT_RETRIES
    ) {
      return;
    }

    const alreadyRequestedCurrentPayload =
      requestedInlinePaymentPayloadKeyRef.current === inlinePaymentPayloadKey;

    if (alreadyRequestedCurrentPayload && paymentState?.clientSecret) {
      return;
    }

    const currentRetryCount = retryCount;
    const requestId = piRequestIdRef.current + 1;
    const requestPayloadKey = inlinePaymentPayloadKey;
    const isCurrentRequest = () =>
      piRequestIdRef.current === requestId &&
      latestInlinePaymentPayloadKeyRef.current === requestPayloadKey;
    piRequestIdRef.current = requestId;
    requestedInlinePaymentPayloadKeyRef.current = requestPayloadKey;
    setPaymentState(null);
    setIsCreatingPI(true);

    const createPaymentIntent = async () => {
      try {
        const piParams = {
          currentUser,
          currentListing,
          currentProvider,
          lineItems: updatedLineItems,
        };

        const result = await onCreatePaymentIntent(piParams, transactionId);

        // Ignore late responses from an older request or an older payload revision.
        if (!isCurrentRequest()) {
          return;
        }

        setPaymentState({
          clientSecret: result.clientSecret,
          stripePaymentIntentId: result.stripePaymentIntentId,
          customerTotal: result.customerTotal,
          applicationTotal: result.applicationTotal,
          changedTotals: result.changedTotals,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);

        if (isCurrentRequest()) {
          // Stop retrying after the capped number of attempts for this payload.
          if (currentRetryCount >= MAX_INLINE_PAYMENT_RETRIES) {
            setPaymentError({
              type: 'create-payment-intent-failed',
              attempts: currentRetryCount + 1,
            });
          } else {
            setRetryCount(currentRetryCount + 1);
          }
        }
      } finally {
        if (isCurrentRequest()) {
          setIsCreatingPI(false);
        }
      }
    };

    createPaymentIntent();
  }, [
    currentListing,
    currentProvider,
    currentUser,
    inlinePaymentPayloadKey,
    isCreatingPI,
    paymentError,
    needsInlinePayment,
    onCreatePaymentIntent,
    paymentState?.clientSecret,
    retryCount,
    transactionId,
    updatedLineItems,
  ]);

  const isAwaitingInlinePayment = needsInlinePayment && !paymentState?.clientSecret;

  return {
    paymentState,
    isCreatingPI,
    paymentError,
    isAwaitingInlinePayment,
    clearPaymentState: () => {
      requestedInlinePaymentPayloadKeyRef.current = null;
      setPaymentState(null);
      setPaymentError(null);
      setRetryCount(0);
    },
  };
};

export default useInlinePaymentIntent;
